import {useAgencyPaymentMethodsQuery} from "@/api/agencyApi";
import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import {PAYMENT_KEYS} from "@/api/paymentApi";
import type {InvoiceSerializer} from "@/generated/DjangoTypes";
import {usePayment} from "@/hooks/usePayment";
import {useIsAdmin, useSelectedCompany, useSelectedCompanyId} from "@/hooks/useUserHooks";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import {getCampaignApplicationQueryKeys} from "@/store/campaignManagement";
import {useParams} from "@tanstack/react-router";
import {sumBy} from "lodash";
import {useState} from "react";

export const useFailedInvoicePayment = ({invoices, onPaid}: {invoices: InvoiceSerializer[]; onPaid(): void}) => {
  const {companyId} = useParams({strict: false});
  const isAdmin = useIsAdmin();
  const selectedCompany = useSelectedCompany();
  const selectedCompanyId = useSelectedCompanyId();
  const {payInvoices, invoicePayIsLoading, setDefaultPaymentMethod, setDefaultLoading} = usePayment();
  const [showAddNewPaymentMethodForm, setShowAddNewPaymentMethodForm] = useState(false);
  const [showUpdatePaymentMethodForm, setShowUpdatePaymentMethodForm] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const {data: paymentMethods, isPending: isLoadingPaymentMethods} = useAgencyPaymentMethodsQuery({
    agencyId: selectedCompany?.agency ?? undefined,
  });

  const currency = invoices[0]?.currency ?? "CHF";
  const invoiceIds = invoices.map(invoice => invoice.id);
  const totalAmount = sumBy(invoices, invoice => invoice.amount ?? 0);

  const pay = async () => {
    if (!selectedPaymentMethodId) {
      return;
    }

    capturePostHogEvent(PostHogEvents.PaymentRetryAttempted, {
      currency,
      invoiceCount: invoices.length,
      totalAmount,
    });
    await setDefaultPaymentMethod({paymentMethodId: selectedPaymentMethodId});
    await payInvoices({invoiceIds});
    setTimeout(() => {
      const params = `company_profile_id=${isAdmin ? companyId : selectedCompanyId}`;
      [PAYMENT_KEYS.qRegularInvoiceList(params), PAYMENT_KEYS.qRegularInvoiceListPdfExport(params)].map(queryKey =>
        queryClient.invalidateQueries({queryKey}),
      );
      const campaignIds = [
        ...new Set(invoices.filter(invoice => invoice.campaign?.id).map(invoice => invoice.campaign!.id)),
      ];
      campaignIds.forEach(campaignId => {
        [
          ...getCampaignApplicationQueryKeys(campaignId),
          CAMPAIGN_KEYS.qCampaign(campaignId),
          CAMPAIGN_KEYS.qCampaignManagement(campaignId),
          CAMPAIGN_KEYS.qCampaignPreparationApplications(campaignId),
        ].map(queryKey => queryClient.invalidateQueries({queryKey}));
      });

      [...new Set(invoices.map(invoice => invoice.company_profile.id))].forEach(companyProfileId =>
        queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompany(companyProfileId)}),
      );
    }, 2500);
    setSelectedPaymentMethodId(null);
    onPaid();
  };

  return {
    currency,
    invoiceIds,
    isLoadingPaymentMethods,
    isPayDisabled: !selectedPaymentMethodId || invoicePayIsLoading || setDefaultLoading,
    pay,
    paymentMethods,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    setShowAddNewPaymentMethodForm,
    setShowUpdatePaymentMethodForm,
    showAddNewPaymentMethodForm,
    showUpdatePaymentMethodForm,
    totalAmount,
  };
};

export type TFailedInvoicePayment = ReturnType<typeof useFailedInvoicePayment>;
